import {Injectable} from "@angular/core";
import {nanoid} from "nanoid";
import {environment} from "@environments/environment";
import {Uppy} from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import Dashboard from "@uppy/dashboard/lib/Dashboard";
import Webcam from "@uppy/webcam/lib/Webcam";
import {BehaviorSubject} from "rxjs";
import {TranslocoService} from "@ngneat/transloco";

@Injectable({
    providedIn: 'root'
})
export class ImageUploadService {
    private maxFileSize = 50 * Math.pow(1024, 2);
    private maxNumberOfFiles = 1;
    private minNumberOfFiles = 1;

    uploadedImgUrl$ = {};

    constructor(
        private transLoco: TranslocoService
    ) {
    }

    public createUploader(surveyId: string, questionKey: string, isSmallScreen: boolean) {
        this.uploadedImgUrl$[questionKey] ??= new BehaviorSubject(null);

      const uppy = new Uppy(
            {
                id: nanoid(),
                autoProceed: true,
                restrictions: {
                    allowedFileTypes: ['image/*'],
                    maxFileSize: this.maxFileSize,
                    maxNumberOfFiles: this.maxNumberOfFiles,
                    minNumberOfFiles: this.minNumberOfFiles,
                },
            })
            .use(Dashboard,
                {
                    inline: true,
                    target: `#drag-drop-area-${questionKey}`,
                    note: `${this.transLoco.translate('Taille maximale: ')}${this.maxFileSize / Math.pow(1024, 2)} ${this.transLoco.translate('Mo')} - ${this?.maxNumberOfFiles} ${this.transLoco.translate('fichier maximum.')}`,
                    showProgressDetails: true,
                    locale: {
                        strings: {
                            browseFiles: this.transLoco.translate('parcourir'),
                            dropPasteImportFiles: this.transLoco.translate('Glissez-déposez une image ou cliquez pour importer depuis: '),
                            myDevice: this.transLoco.translate('Mon appareil'),
                        } as any,
                    },

                })


            .use(Webcam, {
                locale: {
                    strings: {
                        pluginNameCamera: this.transLoco.translate('Ma caméra'),
                        noCameraTitle: this.transLoco.translate('Caméra non disponible'),
                        noCameraDescription: this.transLoco.translate('Pour prendre des photos ou enregistrer des vidéos, veuillez connecter un appareil photo'),
                        recordingStoppedMaxSize: this.transLoco.translate('Enregistrement arrêté car la taille du fichier est sur le point de dépasser la limite'),
                        submitRecordedFile: this.transLoco.translate('Soumettre le fichier enregistré'),
                        discardRecordedFile: this.transLoco.translate('Jeter le fichier enregistré'),
                        smile: this.transLoco.translate('Souriez !'),
                        takePicture: this.transLoco.translate('Prendre une photo'),
                        startRecording: this.transLoco.translate('Commencer l\'enregistrement vidéo'),
                        stopRecording: this.transLoco.translate('Arrêter l\'enregistrement vidéo'),
                        recordingLength: this.transLoco.translate('Durée de l\'enregistrement %{recording_length}'),
                        allowAccessTitle: this.transLoco.translate('Veuillez autoriser l\'accès à votre caméra'),
                        allowAccessDescription: this.transLoco.translate('Pour prendre des photos ou enregistrer des vidéos avec votre caméra, veuillez autoriser l\'accès à la caméra pour ce site.'),
                    },
                },
                modes: ['picture'],
            })

            .use(Transloadit, {
                assemblyOptions: {
                    params: {
                        auth: {key: environment.medias.transloadIt_key},
                        steps: {
                            ':original': {
                                robot: '/upload/handle'
                            },
                            viruscheck: {
                                use: ':original',
                                robot: '/file/virusscan',
                                error_on_decline: true
                            },
                            document_file: {
                                use: ['viruscheck'],
                                robot: '/file/filter',
                                result: true,
                                accepts: [['${file.mime}', 'regex', 'document'],],
                                error_on_decline: false
                            },
                            exportUpload: {
                                use: [':original', 'document_file'],
                                bundle_steps: true,
                                robot: '/s3/store',
                                credentials: 'S3 Approbation',
                                bucket: `inbe-${environment?.production ? 'production' : 'approbation'}`,
                                path: 'responses/${fields.surveyId}/${file.id}.${file.ext}',
                                url_prefix: `https://inbe-${environment?.production ? 'production' : 'approbation'}.s3.us-west-2.amazonaws.com/`,
                                acl: 'public-read',
                                sign_urls_for: -1
                            },
                        },
                    },
                    fields: {surveyId}
                },

                waitForEncoding: true,
                waitForMetadata: true,
            })
            .on('transloadit:complete', (assembly) => {
                const newUrl = assembly.uploads[0].ssl_url.replace('s3.us-west-2.amazonaws.com', 'imgix.net');
                this.uploadedImgUrl$[questionKey].next(newUrl);
            });
        return uppy;

    }

    deleteUpload(questionKey: string) {
        this.uploadedImgUrl$[questionKey].next(null);
    }
}
